import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Avatar, Space, Typography } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  BankOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  DollarOutlined,
  WalletOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'super_admin') return '/super-admin';
    if (user.role === 'owner') return '/owner';
    if (user.role === 'employee' && user.employeeType !== 'accountant') return '/tasks';
    return '/employee';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const getMenuItems = () => {
    const items = [
      {
        key: getDashboardPath(),
        icon: <DashboardOutlined />,
        label: <Link to={getDashboardPath()}>Dashboard</Link>,
      },
    ];

    if (user?.role === 'super_admin') {
      items.push({
        key: '/super-admin',
        icon: <TeamOutlined />,
        label: <Link to="/super-admin">Owner Management</Link>,
      });
    }

    if (user?.role === 'owner') {
      items.push(
        { key: '/hostels', icon: <BankOutlined />, label: <Link to="/hostels">Hostels</Link> },
        { key: '/bookings', icon: <CalendarOutlined />, label: <Link to="/bookings">Bookings</Link> },
        { key: '/tasks', icon: <CheckSquareOutlined />, label: <Link to="/tasks">Tasks</Link> },
        { key: '/revenue', icon: <DollarOutlined />, label: <Link to="/revenue">Revenue</Link> },
        { key: '/employee-payments', icon: <WalletOutlined />, label: <Link to="/employee-payments">Payments</Link> },
        { key: '/bills', icon: <FileTextOutlined />, label: <Link to="/bills">Bills</Link> }
      );
    }

    if (user?.role === 'employee') {
      if (user.employeeType === 'accountant') {
        items.push(
          { key: '/hostels', icon: <BankOutlined />, label: <Link to="/hostels">Hostels</Link> },
          { key: '/bookings', icon: <CalendarOutlined />, label: <Link to="/bookings">Bookings</Link> },
          { key: '/tasks', icon: <CheckSquareOutlined />, label: <Link to="/tasks">Tasks</Link> },
          { key: '/employee-payments', icon: <WalletOutlined />, label: <Link to="/employee-payments">Payments</Link> },
          { key: '/bills', icon: <FileTextOutlined />, label: <Link to="/bills">Bills</Link> }
        );
      } else {
        items.push({ key: '/tasks', icon: <CheckSquareOutlined />, label: <Link to="/tasks">Tasks</Link> });
      }
    }

    return items;
  };

  const selectedKey = () => {
    const path = location.pathname;
    if (path === '/super-admin' || path === '/owner' || path === '/employee') return getDashboardPath();
    return path;
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="dark"
        width={240}
        breakpoint="lg"
        collapsedWidth={0}
        onBreakpoint={(broken) => setCollapsed(!!broken)}
      >
        <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', paddingLeft: collapsed ? 0 : 24 }}>
          <Link to={getDashboardPath()} style={{ color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 18 }}>
            {collapsed ? 'HMS' : 'Hostel MS'}
          </Link>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey()]}
          items={getMenuItems()}
          style={{ marginTop: 8 }}
        />
      </Sider>
      <Layout>
        <Header className="app-layout-header">
          <Space>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'app-layout-trigger',
              onClick: () => setCollapsed(!collapsed),
            })}
          </Space>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }} wrap={false}>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              <Text strong className="app-layout-username">{user?.name || 'User'}</Text>
            </Space>
          </Dropdown>
        </Header>
        <Content className="app-layout-content">{children}</Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
